import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Banknote,
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  MapPin,
  Scale,
  ShieldCheck,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { orgScopedPath } from "@/lib/orgDoor";

export type FeatureBannerKey = "deal-matching" | "financing" | "sba-calculator" | "valuation-calculator";

export interface HeroBannerSlide {
  key: FeatureBannerKey;
  to: string;
}

interface FeatureBannerContent {
  eyebrow: string;
  title: string;
  accent: string;
  body: string;
  cta: string;
  chips: { icon: LucideIcon; label: string }[];
  /** Niva cutout (transparent background), a different photo per banner. */
  art: string;
  footnote?: string;
}

/**
 * Feature banner copy. Rendered as live markup (Poppins headline, like hero
 * slide 1): the old full-bleed .webp banners baked in a serif headline, the
 * logo and the site URL.
 */
const FEATURE_BANNERS: Record<FeatureBannerKey, FeatureBannerContent> = {
  "deal-matching": {
    eyebrow: "Your Buy-Box. Your next business.",
    title: "Stop searching.",
    accent: "Start matching.",
    body: "Set your industry, location and budget in your Buy-Box. Let AI surface opportunities that fit your goals.",
    cta: "AI Deal Matching",
    chips: [
      { icon: Building2, label: "Industry" },
      { icon: MapPin, label: "Location" },
      { icon: Wallet, label: "Budget" },
    ],
    art: "/images/home-banners/deal-matching-niva.webp",
  },
  financing: {
    eyebrow: "Buy with confidence",
    title: "Your next business.",
    accent: "Know your buying power.",
    body: "Explore acquisition financing with Duneland. Start with a no-cost pre-qualification check.",
    cta: "Explore Financing",
    chips: [{ icon: FileText, label: "No-cost pre-qualification" }],
    art: "/images/home-banners/financing-niva.webp",
    footnote: "Financing subject to lender review and approval.",
  },
  "sba-calculator": {
    eyebrow: "SBA 7(a) Acquisition Calculator",
    title: "Run the numbers.",
    accent: "Buy with certainty.",
    body: "Model debt service, cash to close and DSCR on any deal. See if it fits SBA 7(a) before you make an offer.",
    cta: "SBA Calculator",
    chips: [
      { icon: BarChart3, label: "DSCR" },
      { icon: Banknote, label: "Cash required" },
      { icon: ShieldCheck, label: "SBA eligibility" },
    ],
    art: "/images/home-banners/sba-calculator-niva.webp",
  },
  "valuation-calculator": {
    eyebrow: "Business Valuation Calculator",
    title: "Know what it’s worth.",
    accent: "Before you offer.",
    body: "Work out SDE, EBITDA and a defensible valuation range for any business in minutes.",
    cta: "Valuation Calculator",
    chips: [
      { icon: TrendingUp, label: "SDE" },
      { icon: FileText, label: "EBITDA" },
      { icon: Scale, label: "Value range" },
    ],
    art: "/images/home-banners/valuation-calculator-niva.webp",
  },
};

/**
 * The feature banners that slide on the marketplace hero. Every feature here
 * lives under a buyer-only route (buyer + admin), so only users who can open
 * them get slides. The BL Academy banner is retired for now; its artwork stays
 * in public/images/home-banners/bl-academy.webp for when it returns.
 */
export function featureBanners(orgSlug: string | null | undefined, canUseBuyerTools: boolean): HeroBannerSlide[] {
  return [
    ...(canUseBuyerTools
      ? [
          { key: "deal-matching" as const, to: orgScopedPath("/buyer/deal-scoring", orgSlug) },
          { key: "financing" as const, to: orgScopedPath("/buyer/funding", orgSlug) },
          { key: "sba-calculator" as const, to: orgScopedPath("/buyer/sba-calculator", orgSlug) },
          { key: "valuation-calculator" as const, to: orgScopedPath("/buyer/valuation-calculator", orgSlug) },
        ]
      : []),
  ];
}

/**
 * One feature banner, styled like hero slide 1 and pinned to the Dark · Gold
 * look in both themes. `compact` (the mobile dashboard strip) drops the chips
 * and footnote to keep the card short.
 */
export function FeatureBanner({ slide, compact = false, className }: { slide: HeroBannerSlide; compact?: boolean; className?: string }) {
  const b = FEATURE_BANNERS[slide.key];
  return (
    <Link
      to={slide.to}
      data-theme="dark"
      aria-label={`${b.title} ${b.accent} ${b.cta}`}
      className={cn("public-hero-shell hero-niva block h-full w-full rounded-[1.85rem] shadow-glow", className)}
    >
      <div className="public-grid-lattice absolute inset-0 opacity-45" />
      {/* Niva stands on the bottom edge; hidden below lg, where the text
          column needs the full width. */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[40%] lg:block">
        <div className="absolute left-1/2 top-[12%] aspect-square h-[88%] -translate-x-1/2 rounded-full border border-primary/30 bg-[radial-gradient(circle,hsl(41_60%_45%/0.25)_0%,transparent_70%)]" />
        <img
          src={b.art}
          alt=""
          className="absolute inset-0 h-full w-full select-none object-contain object-bottom"
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        <span className="absolute bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-primary/50 bg-black/60 px-4 py-1.5 text-sm text-foreground backdrop-blur-sm">
          <span className="font-semibold text-primary">Niva</span>
          <span className="text-primary/60">&bull;</span>
          Your AI Deal Assistant
        </span>
      </div>

      <div className={cn("relative z-10 flex h-full flex-col justify-center lg:w-[60%]", compact ? "px-5 py-5" : "px-6 py-7 sm:px-10 sm:py-8")}>
        <span className="inline-flex w-fit items-center gap-2.5 rounded-full border border-primary/50 bg-black/20 px-4 py-1.5 text-[0.7rem] font-bold uppercase tracking-[0.16em] text-primary sm:text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {b.eyebrow}
        </span>

        <h2 className={cn("heading-bold mt-4 font-display leading-[1.04] text-foreground", compact ? "text-2xl" : "text-3xl sm:text-4xl xl:text-[2.75rem]")}>
          {b.title}
          <span className="text-gradient block pb-1">{b.accent}</span>
        </h2>

        <p className={cn("mt-2 max-w-2xl leading-relaxed text-foreground/85", compact ? "text-sm" : "text-sm sm:text-base")}>{b.body}</p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {/* A span, not a <Button>: the whole banner is already the link. */}
          <span className="inline-flex h-11 items-center gap-2 rounded-xl bg-gold-gradient px-5 text-sm font-semibold text-primary-foreground shadow-glow">
            {b.cta}
            <ArrowRight className="h-4 w-4" />
          </span>
          {!compact &&
            b.chips.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-black/20 px-3 py-1.5 text-xs text-foreground sm:text-sm">
                <Icon className="h-4 w-4 text-primary" />
                {label}
              </span>
            ))}
        </div>

        {!compact && b.footnote && <p className="mt-3 text-xs text-muted-foreground">{b.footnote}</p>}
      </div>
    </Link>
  );
}

const AUTO_ADVANCE_MS = 7000;
const SWIPE_THRESHOLD_PX = 40;

/**
 * Home hero slider: the live hero markup (optional) is slide 1, followed by the
 * feature banners, each linking to its feature. Off-screen slides are `inert` so
 * their links aren't tabbable.
 *
 * From lg up the slides sit side by side in a flex track and every slide
 * stretches to the tallest one, so the page never jumps as it advances.
 *
 * Below lg that stretch is wrong: the text hero stacks into a tall column
 * while the banners are much shorter, so each banner floated in a big empty
 * dark box. There the slides keep their own height and the viewport follows
 * the active one instead — and auto-advance is off, so the page below only
 * ever moves when the buyer swipes or taps.
 */
export function HeroCarousel({ hero, banners }: { hero?: ReactNode; banners: HeroBannerSlide[] }) {
  // Without a hero (the admin listings page) the banners slide alone.
  const count = banners.length + (hero ? 1 : 0);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [compact, setCompact] = useState(() => window.matchMedia?.("(max-width: 1023px)").matches ?? false);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 1023px)");
    if (!mq) return;
    const sync = () => setCompact(mq.matches);
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Track the active slide's height (images load late, text reflows).
  useLayoutEffect(() => {
    const el = slideRefs.current[index];
    if (!compact || !el) return;
    const measure = () => setHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, index]);

  const go = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  useEffect(() => {
    if (paused || compact || count < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setTimeout(() => go(index + 1), AUTO_ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [index, paused, compact, count, go]);

  const slides: ReactNode[] = [
    ...(hero ? [hero] : []),
    ...banners.map((b) => <FeatureBanner key={b.key} slide={b} />),
  ];

  return (
    <div
      className="relative"
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") go(index - 1);
        else if (e.key === "ArrowRight") go(index + 1);
      }}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        const start = touchStartX.current;
        touchStartX.current = null;
        if (start === null) return;
        const dx = e.changedTouches[0].clientX - start;
        if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) go(index + (dx < 0 ? 1 : -1));
      }}
    >
      <div
        className="overflow-hidden rounded-[1.85rem] transition-[height] duration-500 ease-out motion-reduce:transition-none"
        style={compact && height ? { height } : undefined}
      >
        <div
          className="flex items-start transition-transform duration-700 ease-out motion-reduce:transition-none lg:items-stretch"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={i}
              ref={(el) => { slideRefs.current[i] = el; }}
              className="w-full shrink-0"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${count}`}
              aria-hidden={i !== index}
              inert={i !== index}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {count > 1 && (
        <>
          {/* Arrows sit beside the dots, not over the slide, so they never
              cover the hero copy. */}
          <div className="mt-3 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="Previous slide"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors hover:bg-primary/15"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-current={i === index}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === index ? "w-6 bg-primary" : "w-1.5 bg-primary/30 hover:bg-primary/60",
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="Next slide"
              className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/30 text-primary transition-colors hover:bg-primary/15"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
